// advancePaymentUtils.js - JavaScript implementation matching Python backend logic

// Configuration matching backend
const ADVANCE_INTEREST_RATE = 0.1; // 10% interest rate
const ADVANCE_FIXED_FINE = 10; // 10 KES fixed fine

/**
 * Split the advance payment amount into principal (pay_advance) and interest (pay_advance_interest)
 * using simultaneous equations - matches Python backend logic exactly.
 * 
 * @param {number} currentBalance - Current advance loan balance
 * @param {number} amountForAdvancePayment - Total amount to be allocated for advance payment
 * @returns {Object} Object with pay_advance, pay_advance_interest, and remaining_balance
 */
export function splitAdvancePayment(currentBalance, amountForAdvancePayment) {
    // Handle case where no advance balance exists
    if (currentBalance <= 0) {
        return {
            pay_advance: 0,
            pay_advance_interest: 0,
            remaining_balance: 0
        };
    }
    
    // Handle case where payment amount is 0
    if (amountForAdvancePayment <= 0) {
        return {
            pay_advance: 0,
            pay_advance_interest: 0,
            remaining_balance: currentBalance
        };
    }
    
    // Case 1: Full payment - no interest required
    if (amountForAdvancePayment >= currentBalance) {
        return {
            pay_advance: currentBalance,
            pay_advance_interest: 0,
            remaining_balance: 0
        };
    }
    
    // Case 2: Partial payment - solve simultaneous equations
    const interestComponent = currentBalance * ADVANCE_INTEREST_RATE / (1 + ADVANCE_INTEREST_RATE);
    
    let payAdvance = (amountForAdvancePayment - interestComponent - ADVANCE_FIXED_FINE) * (1 + ADVANCE_INTEREST_RATE) / 1;
    
    // Ensure pay_advance doesn't exceed current_balance or go negative
    payAdvance = Math.max(0, Math.min(payAdvance, currentBalance));
    
    let payAdvanceInterest;
    
    if (payAdvance >= currentBalance) {
        // Full payment case
        payAdvance = currentBalance;
        payAdvanceInterest = 0;
    } else {
        // Calculate interest for remaining balance
        const remainingBalance = currentBalance - payAdvance;
        const currentPrincipal = remainingBalance / (1 + ADVANCE_INTEREST_RATE);
        payAdvanceInterest = currentPrincipal * ADVANCE_INTEREST_RATE + ADVANCE_FIXED_FINE;
        
        // Round to nearest KES
        payAdvanceInterest = Math.round(payAdvanceInterest);
    }
    
    return {
        pay_advance: Math.round(payAdvance * 100) / 100, // Round to 2 decimal places
        pay_advance_interest: payAdvanceInterest,
        remaining_balance: currentBalance - payAdvance
    };
}

/**
 * Calculate advance payment split for preview with validation
 * 
 * @param {number} currentBalance - Current advance loan balance
 * @param {number} amountForAdvancePayment - Total amount to allocate for advance payment
 * @returns {Object} Object with split amounts and validation info
 */
export function calculateAdvancePaymentSplit(currentBalance, amountForAdvancePayment) {
    if (currentBalance <= 0) {
        return {
            current_balance: 0,
            pay_advance: 0,
            pay_advance_interest: 0,
            remaining_balance: 0,
            is_valid: true,
            message: 'No outstanding advance loan balance',
            has_penalty: false,
            penalty_amount: 0
        };
    }
    
    // Check if payment exceeds balance
    if (amountForAdvancePayment > currentBalance) {
        return {
            current_balance: currentBalance,
            pay_advance: currentBalance,
            pay_advance_interest: 0,
            remaining_balance: 0,
            is_valid: false,
            message: `Payment amount (${amountForAdvancePayment.toLocaleString()}) exceeds current balance (${currentBalance.toLocaleString()})`,
            has_penalty: false,
            penalty_amount: 0
        };
    }
    
    // Calculate minimum required for no penalty: (current_balance / 1.1 * 0.1 + 10)
    const minimumRequiredAmount = (currentBalance / 1.1) * 0.1 + ADVANCE_FIXED_FINE;
    const hasPenalty = amountForAdvancePayment > 0 && amountForAdvancePayment < minimumRequiredAmount;
    const penaltyAmount = hasPenalty ? (minimumRequiredAmount - amountForAdvancePayment) : 0;
    
    // Split the payment
    const split = splitAdvancePayment(currentBalance, amountForAdvancePayment);
    
    return {
        current_balance: currentBalance,
        pay_advance: split.pay_advance,
        pay_advance_interest: split.pay_advance_interest,
        remaining_balance: split.remaining_balance,
        is_valid: true,
        message: 'Payment split calculated successfully',
        has_penalty: hasPenalty,
        penalty_amount: Math.round(penaltyAmount * 100) / 100,
        minimum_required_amount: Math.round(minimumRequiredAmount * 100) / 100
    };
}

/**
 * Format currency for display
 * 
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount) {
    return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
    }).format(amount);
}

/**
 * Test function to verify the logic works correctly
 */
export function testSplitLogic() {
    console.log("=== Advance Payment Split Test ===");
    
    const testCases = [
        [22000, 22000],  // Full payment
        [22000, 9010],   // Partial payment
        [22000, 2010],   // Interest only equivalent
        [22000, 0],      // No payment
        [0, 1000],       // No balance
        [22000, 25000],  // Overpayment
        [22000, 1500],   // Below minimum threshold
    ];
    
    testCases.forEach(([balance, payment], index) => {
        console.log(`\nTest Case ${index + 1}:`);
        console.log(`Current Balance: ${balance}`);
        console.log(`Payment Amount: ${payment}`);
        
        const result = calculateAdvancePaymentSplit(balance, payment);
        
        console.log(`Split - Principal: ${result.pay_advance}, Interest: ${result.pay_advance_interest}`);
        console.log(`Total Split: ${result.pay_advance + result.pay_advance_interest}`);
        console.log(`Remaining Balance: ${result.remaining_balance}`);
        console.log(`Minimum Required: ${result.minimum_required_amount}`);
        console.log(`Has Penalty: ${result.has_penalty}`);
        if (result.has_penalty) {
            console.log(`Penalty Amount: ${result.penalty_amount}`);
        }
    });
}

// Run test if this file is executed directly (for Node.js testing)
if (typeof require !== 'undefined' && require.main === module) {
    testSplitLogic();
}